import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { conRespaldo } from '@/lib/campo/loUltimoQueSeSupo'
import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import { identificadorUnico } from '@/shared/utilidades/identificador'
import { mapConLimite } from '@/shared/utilidades/concurrencia'

/**
 * LAS FOTOS DE LA VISITA.
 *
 * No son un adorno del formulario: son lo que se vio antes de tocar nada —el
 * tablero quemado, el cable sin canalizar, el medidor—. De ahí sale la
 * cotización, y cuando el cliente discute el precio tres semanas después son
 * la prueba de cómo estaba la instalación cuando se llegó.
 *
 * Viven en el bucket PRIVADO `evidencias`, bajo `os/<orden>/`, que es prefijo
 * propio: las políticas que ya había sobre ese bucket cuelgan de la orden de
 * TRABAJO y del colaborador asignado, y una orden de servicio recién creada no
 * tiene ni lo uno ni lo otro.
 *
 * Al ser privado no hay URL pública: se ven con una firma temporal que se
 * renueva sola.
 */

const BUCKET = 'evidencias'

export type FotoOrden = {
  id: string
  ruta: string
  creado_en: string
  /** La hora de la obra (del sello). Puede diferir de creado_en si subió tarde. */
  tomada_en: string | null
  /** null = sin verificar; true = bytes intactos; false = posible alteración. */
  verificada: boolean | null
  quien: string
}

/**
 * Sube una foto y deja la fila que la referencia. Devuelve su ruta.
 *
 * `meta` llega cuando la foto viene de la cola local: trae su id —que se usa
 * como nombre en el bucket para que reintentar no duplique—, su hash y la hora
 * en que se tomó. Sin `meta` es una subida directa (cuando hay señal y no pasó
 * por la cola).
 */
export async function subirFotoDeOrden(
  ordenServicioId: string,
  archivo: Blob,
  meta?: { id?: string; hash?: string; tomadaEn?: string },
): Promise<string> {
  const ext = archivo.type === 'image/png' ? 'png' : archivo.type === 'image/webp' ? 'webp' : 'jpg'
  const ruta = `os/${ordenServicioId}/${meta?.id ?? identificadorUnico()}.${ext}`

  const { error: fallo } = await supabase.storage
    .from(BUCKET)
    // upsert cuando trae id: un reintento sube el MISMO archivo a la MISMA
    // ruta sin romper. Sin id, no se pisa nada.
    .upload(ruta, archivo, {
      contentType: archivo.type || 'image/jpeg',
      upsert: Boolean(meta?.id),
    })

  if (fallo) throw new Error(`No se pudo subir la foto: ${fallo.message}`)

  const { data: sesion } = await supabase.auth.getUser()

  const { error } = await supabase.from('fotos_orden_servicio').insert({
    orden_servicio_id: ordenServicioId,
    ruta,
    // La política lo exige: nadie sube a nombre de otro.
    subida_por: sesion.user?.id,
    // La huella y la hora de la obra viajan con la foto desde la cola: el hash
    // deja al cron verificar-foto (0172) comprobar que los bytes no cambiaron, y
    // tomada_en guarda cuándo se disparó, aparte de creado_en (la de subida).
    // Sin meta (subida directa, sin cola) quedan null y el cron las ignora.
    hash: meta?.hash ?? null,
    tomada_en: meta?.tomadaEn ?? null,
  })

  if (error) {
    // La fila no quedó: la imagen huérfana en el bucket solo ocupa espacio y
    // confunde a quien audite. Se limpia.
    await supabase.storage.from(BUCKET).remove([ruta])
    throw new Error(mensajeDeError(error))
  }

  return ruta
}

export function useFotosDeLaOrden(ordenServicioId: string | null) {
  return useQuery({
    queryKey: ['fotos-orden', ordenServicioId],
    enabled: Boolean(ordenServicioId),
    queryFn: async (): Promise<FotoOrden[]> => {
      if (!ordenServicioId) return []

      // CON RESPALDO. Sin señal la lista salía vacía y el paso 3 decía
      // «faltan 5 fotos» —y el cotizador, que faltaba la foto— de una orden
      // que ya las tenía subidas (auditoría sin conexión, oct 2026). Lo que
      // se guarda es la lista, no las imágenes: alcanza para contarlas.
      return conRespaldo<FotoOrden[]>(`fotos-orden.${ordenServicioId}`, async () => {
        const { data, error } = await supabase.rpc('fn_fotos_de_la_orden', {
          p_orden_servicio_id: ordenServicioId,
        })

        if (error) throw new Error(mensajeDeError(error))
        return (data ?? []) as FotoOrden[]
      })
    },
  })
}

/** URLs firmadas para verlas. La firma dura una hora y se renueva antes. */
export function useUrlesDeFotos(rutas: string[]) {
  return useQuery({
    queryKey: ['fotos-orden-urls', ...rutas],
    enabled: rutas.length > 0,
    staleTime: 50 * 60 * 1000,
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase.storage.from(BUCKET).createSignedUrls(rutas, 3600)
      if (error) throw new Error(error.message)
      return (data ?? []).map((d) => d.signedUrl).filter((u): u is string => Boolean(u))
    },
  })
}

export function useSubirFotos() {
  const cliente = useQueryClient()

  return useMutation({
    mutationFn: async ({
      ordenServicioId,
      archivos,
    }: {
      ordenServicioId: string
      archivos: File[]
    }): Promise<number> => {
      // De a varias a la vez (tope de tres): más rápido que en serie cuando son
      // varias, sin saturar una conexión mala.
      await mapConLimite(archivos, 3, (archivo) => subirFotoDeOrden(ordenServicioId, archivo))
      return archivos.length
    },
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['fotos-orden'] })
    },
  })
}

/** Quitar una foto: borra la fila y la imagen. */
export function useBorrarFoto() {
  const cliente = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ruta }: { id: string; ruta: string }): Promise<void> => {
      const { error } = await supabase.from('fotos_orden_servicio').delete().eq('id', id)
      if (error) throw new Error(mensajeDeError(error))

      // La imagen después de la fila: si falla el borrado del archivo queda
      // basura en el bucket, pero si fallara al revés quedaría una fila
      // apuntando a una imagen que ya no existe, que es peor —la pantalla
      // mostraría un hueco sin explicación—.
      await supabase.storage.from(BUCKET).remove([ruta])
    },
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['fotos-orden'] })
    },
  })
}
